import {
  API_ROUTES,
  type ExplainDecisionRequest,
  taxDecisionSchema,
  taxInsightsSchema,
  type TaxInsightsQuery,
  taxRulePackSchema,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Tax queries. */

export const taxKeys = {
  all: (organizationId: string) => ['tax', organizationId] as const,
  insights: (organizationId: string, query: TaxInsightsQuery) =>
    ['tax', organizationId, 'insights', query] as const,
  decisions: (organizationId: string, recordId: string) =>
    ['tax', organizationId, 'decisions', recordId] as const,
} as const;

export const taxInsightsQueryOptions = (organizationId: string, query: TaxInsightsQuery) =>
  queryOptions({
    queryKey: taxKeys.insights(organizationId, query),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.tax.insights(organizationId)}?from=${query.from}&to=${query.to}`,
        schema: taxInsightsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export const taxDecisionsQuery = (organizationId: string, recordId: string) =>
  queryOptions({
    queryKey: taxKeys.decisions(organizationId, recordId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.tax.decisions(organizationId, recordId),
        schema: z.array(taxDecisionSchema),
        signal,
      }),
  });

export const taxPacksQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['tax', organizationId, 'packs'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.tax.packs(organizationId),
        schema: z.array(taxRulePackSchema),
        signal,
      }),
    staleTime: 300_000,
  });

export async function verifyTaxPack(organizationId: string, packId: string, note: string) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.tax.verifyPack(organizationId, packId),
    schema: z.array(taxRulePackSchema),
    body: { note },
  });
}

export async function explainTaxDecision(
  organizationId: string,
  decisionId: string,
  body: ExplainDecisionRequest = {},
) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.tax.explain(organizationId, decisionId),
    schema: z.object({ explanation: z.string(), model: z.string() }),
    body,
  });
}
